import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Chip,
  Tooltip,
} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { useQuery } from "@apollo/client/react";
import {
  GET_EVENT_LIST,
  GET_INSTRUCTOR_LIST,
  GET_MY_EVENT_BOOKINGS,
} from "../graphql/query";
import EventPurchaseDrawer from "./EventPurchaseDrawer";
import CenteredLoader from "./CenteredLoader";
import CustomSnackbar from "./Snackbar";
import themestyle from "../theme";
import { useCart } from "../context/CartContext";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CustomPaginationActions from "./CustomPaginationActions";
import EventToolBar from "./EventToolBar";
import { formatTo12Hour } from "../utils/time";
import { isEventOfferCurrentlyActive } from "../utils/offerPricing";
import TruncatedTitle from "./TruncatedTitle";
import type { GetEventListResponse } from "../interfaces/event.interface";
import type { GetMyEventBookingsResponse } from "../interfaces/eventBooking.interface";

interface UserEventCardViewProps {
  onView: (id: string) => void;
  refreshTrigger?: number;
}

const PAGE_SIZE_MOBILE = 3;
const PAGE_SIZE_DESKTOP = 9;

const fmtPrice = (amount: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(amount);

const INSTRUCTOR_TOOLTIP_THRESHOLD = 24;
const AMOUNT_FILTER_OPTIONS = {
  ALL: "All Amounts",
  FREE: "Free",
  UNDER_500: "Under ₹500",
  BETWEEN_500_999: "₹500 - ₹999",
  ABOVE_1000: "₹1000 & Above",
} as const;

const BOOKING_FILTER_OPTIONS = {
  ALL: "All Events",
  JOINED: "Joined",
  NOT_JOINED: "Not Joined",
} as const;

const instructorTooltipSx = {
  "& .MuiTooltip-tooltip": {
    background: "linear-gradient(180deg, #FFFFFF 0%, #F6FBEE 100%)",
    color: "#1F2937",
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: 1.45,
    borderRadius: "12px",
    px: 1.4,
    py: 1,
    boxShadow: "0px 12px 28px rgba(90, 168, 57, 0.16)",
    border: "1px solid rgba(163, 214, 92, 0.35)",
    maxWidth: 280,
  },
  "& .MuiTooltip-arrow": {
    color: "#F6FBEE",
  },
};

const UserEventCardView: React.FC<UserEventCardViewProps> = ({
  onView,
  refreshTrigger,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedInstructor, setSelectedInstructor] = useState<string | null>(null);
  const [selectedAmount, setSelectedAmount] = useState<string | null>(null);
  const [selectedBookingStatus, setSelectedBookingStatus] = useState<string | null>(null);
  // Server-side page (1-indexed)
  const [page, setPage] = useState(1);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error" | "info">("success");

  // Purchase drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [selectedEventInstructors, setSelectedEventInstructors] = useState("");

  const rowsPerPage = isMobile ? PAGE_SIZE_MOBILE : PAGE_SIZE_DESKTOP;

  // Reset to page 1 whenever filters or screen size changes
  useEffect(() => {
    setPage(1);
  }, [searchQuery, selectedInstructor, selectedAmount, selectedBookingStatus, isMobile]);

  // --- Instructor list — cache-first so it's only fetched once per session ---
  const { data: instructorsData } = useQuery(GET_INSTRUCTOR_LIST, {
    variables: { page: 1, perPage: 1000 },
    fetchPolicy: "cache-first",
  });

  const instructorMap = useMemo(() => {
    const map: Record<string, string> = {};
    instructorsData?.getInstructorList?.data?.forEach((ins: any) => {
      map[ins.id] = ins.fullname;
    });
    return map;
  }, [instructorsData]);

  // Resolve selected instructor name → id for the backend filter
  const selectedInstructorId = useMemo(() => {
    if (!selectedInstructor) return null;
    return (
      Object.keys(instructorMap).find(
        (id) => instructorMap[id] === selectedInstructor
      ) ?? null
    );
  }, [instructorMap, selectedInstructor]);

  // --- Events — server-side pagination + cache-and-network ---
  const { data, loading, refetch } = useQuery<GetEventListResponse>(GET_EVENT_LIST, {
    variables: {
      page,
      perPage: rowsPerPage,
      search: searchQuery || "",
      instructorId: selectedInstructorId,
    },
    fetchPolicy: "cache-and-network",
  });

  // --- My bookings — cache-and-network so "Joined" state is instant on re-render ---
  const { data: myBookingsData, refetch: refetchMyBookings } =
    useQuery<GetMyEventBookingsResponse>(GET_MY_EVENT_BOOKINGS, {
      fetchPolicy: "cache-and-network",
    });

  const { addItem, isInCart, removeItem, setCartOpen } = useCart();

  useEffect(() => {
    if (refreshTrigger) {
      refetch();
      refetchMyBookings();
    }
  }, [refreshTrigger, refetch, refetchMyBookings]);

  // Build a set of already-booked event IDs for quick lookup
  const bookedEventIds = useMemo(() => {
    const ids = new Set<string>();
    myBookingsData?.getMyEventBookings?.data?.forEach((b) => {
      if (b.status === "CONFIRMED") ids.add(b.eventId);
    });
    return ids;
  }, [myBookingsData]);

  const parseCalendarDate = (dateString?: string | null) => {
    if (!dateString) return null;
    const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  };

  const eventsData = useMemo(() => {
    const rawEvents = data?.getEventList?.data || [];

    const getEventDateTime = (event: any) => {
      const date = parseCalendarDate(event.startDate);
      if (!date) return 0;

      const startTime = event.time?.split("-")?.[0]?.trim();

      if (startTime) {
        const match = startTime.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);

        if (match) {
          let hours = Number(match[1]);
          const minutes = Number(match[2]);
          const period = match[3]?.toUpperCase();

          if (period === "PM" && hours !== 12) hours += 12;
          if (period === "AM" && hours === 12) hours = 0;

          date.setHours(hours, minutes, 0, 0);
        }
      }

      return date.getTime();
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return [...rawEvents].sort((a, b) => {
      const aDate = parseCalendarDate(a.startDate);
      const bDate = parseCalendarDate(b.startDate);

      const aIsPast = aDate ? aDate < today : false;
      const bIsPast = bDate ? bDate < today : false;

      if (!aIsPast && bIsPast) return -1;
      if (aIsPast && !bIsPast) return 1;

      return getEventDateTime(b) - getEventDateTime(a);
    });
  }, [data]);


  const filteredEventsData = useMemo(() => {
    return eventsData.filter((event: any) => {
      const hasActiveOffer = isEventOfferCurrentlyActive(event);
      const finalEventPrice = hasActiveOffer ? event.offerPrice : event.standardPrice;
      const amount = Number(finalEventPrice || 0);

      if (selectedAmount && selectedAmount !== AMOUNT_FILTER_OPTIONS.ALL) {
        if (selectedAmount === AMOUNT_FILTER_OPTIONS.FREE && amount > 0) {
          return false;
        }
        if (selectedAmount === AMOUNT_FILTER_OPTIONS.UNDER_500 && !(amount > 0 && amount < 500)) {
          return false;
        }
        if (selectedAmount === AMOUNT_FILTER_OPTIONS.BETWEEN_500_999 && !(amount >= 500 && amount < 1000)) {
          return false;
        }
        if (selectedAmount === AMOUNT_FILTER_OPTIONS.ABOVE_1000 && amount < 1000) {
          return false;
        }
      }

      const isJoined = bookedEventIds.has(event.id);
      if (selectedBookingStatus && selectedBookingStatus !== BOOKING_FILTER_OPTIONS.ALL) {
        if (selectedBookingStatus === BOOKING_FILTER_OPTIONS.JOINED && !isJoined) {
          return false;
        }
        if (selectedBookingStatus === BOOKING_FILTER_OPTIONS.NOT_JOINED && isJoined) {
          return false;
        }
      }
      return true;
    });
  }, [eventsData, selectedAmount, selectedBookingStatus, bookedEventIds]);

  const totalCount = data?.getEventList?.totalCount ?? 0;
  const totalPages = data?.getEventList?.totalPages ?? 1;
  useEffect(() => {
    filteredEventsData.forEach((event: any) => {
      if (bookedEventIds.has(event.id) && isInCart(event.id)) {
        removeItem(event.id);
      }
    });
  }, [filteredEventsData, bookedEventIds, isInCart, removeItem]);

  // Event name options for the toolbar search dropdown
  const eventOptions = useMemo(() => {
    const uniqueNames = new Set<string>();
    filteredEventsData.forEach((event: any) => {
      if (event.eventName) uniqueNames.add(event.eventName);
    });
    return Array.from(uniqueNames);
  }, [filteredEventsData]);

  const instructorOptions = useMemo(() => {
    return instructorsData?.getInstructorList?.data || [];
  }, [instructorsData]);

  const handleBook = async (event: any, instructorNames: string) => {
    setSelectedEvent(event);
    setSelectedEventInstructors(instructorNames);
    setDrawerOpen(true);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = parseCalendarDate(dateString);
    if (!date || isNaN(date.getTime())) return dateString;
    const dayName = new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(date);
    const monthName = new Intl.DateTimeFormat("en-US", { month: "short" }).format(date);
    const dateNum = date.getDate();
    return `${dayName} : ${dateNum} ${monthName}`;
  };

  const formatEventTime = (timeRange: string) => {
    if (!timeRange) return "";
    if (timeRange.includes("-")) {
      const [start, end] = timeRange.split("-");
      return `${formatTo12Hour(start.trim())} - ${formatTo12Hour(end.trim())}`;
    }
    return formatTo12Hour(timeRange);
  };

  const startItem = (page - 1) * rowsPerPage + 1;
  const endItem = Math.min(page * rowsPerPage, totalCount);

  return (
    <Box sx={{ width: "100%", pb: 4 }}>
      <EventToolBar
        searchQuery={searchQuery}
        onSearchQueryChange={(query) => { setSearchQuery(query); }}
        instructorValue={selectedInstructor}
        amountValue={selectedAmount}
        bookingValue={selectedBookingStatus}
        onInstructorChange={(val) => { setSelectedInstructor(val); }}
        onAmountChange={(val) => { setSelectedAmount(val); }}
        onBookingChange={(val) => { setSelectedBookingStatus(val); }}
        eventOptions={eventOptions}
        eventOptionsLoading={loading}
        instructorOptions={instructorOptions}
        hideBorder={true}
      />

      {loading && filteredEventsData.length === 0 ? (
        <CenteredLoader />
      ) : filteredEventsData.length === 0 ? (
        <Box sx={{ py: 10, textAlign: "center" }}>
          <Typography
            variant="h6"
            color="textSecondary"
            sx={{ fontSize: { xs: "13px", sm: "1rem" } }}
          >
            No events found.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredEventsData.map((event: any) => {
            const formattedDateString = formatDate(event.startDate);
            const instructorNames = Array.isArray(event.instructors)
              ? event.instructors
                .map((id: string) => instructorMap[id] || id)
                .join(", ")
              : instructorMap[event.instructors] || event.instructors || "TBA";
            const shouldShowInstructorTooltip =
              instructorNames.length > INSTRUCTOR_TOOLTIP_THRESHOLD;

            const isBooked = bookedEventIds.has(event.id);
            const getEventEndDateTime = (event: any) => {
              const date = parseCalendarDate(event.startDate);
              if (!date) return null;

              const endTime = event.time?.split("-")?.[1]?.trim();

              if (endTime) {
                const match = endTime.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);

                if (match) {
                  let hours = Number(match[1]);
                  const minutes = Number(match[2]);
                  const period = match[3]?.toUpperCase();

                  if (period === "PM" && hours !== 12) hours += 12;
                  if (period === "AM" && hours === 12) hours = 0;

                  date.setHours(hours, minutes, 0, 0);
                }
              }

              return date;
            };

            const eventEndDateTime = getEventEndDateTime(event);
            const isPast = eventEndDateTime ? eventEndDateTime < new Date() : false;


            // Offer logic
            const hasActiveOffer = isEventOfferCurrentlyActive(event);
            const finalEventPrice = hasActiveOffer
              ? event.offerPrice
              : event.standardPrice;
            const isFreeEvent = !finalEventPrice || finalEventPrice <= 0;
            const eventDiscountPct =
              hasActiveOffer && event.standardPrice > 0
                ? Math.round(
                  ((event.standardPrice - event.offerPrice) /
                    event.standardPrice) *
                  100
                )
                : 0;

            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={event.id} sx={{ display: "flex" }}>
                <Card
                  sx={{
                    borderRadius: "16px",
                    boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.08)",
                    border: "none",
                    position: "relative",
                    overflow: "hidden",
                    backgroundColor: "#fff",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    transition: "transform 0.3s ease, boxShadow 0.3s ease",
                    width: "100%",
                    cursor: !isPast || isBooked ? "pointer" : "default",
                    "&:hover": {
                      transform: !isPast || isBooked ? "translateY(-8px)" : "none",
                      boxShadow: !isPast || isBooked ? "0px 15px 40px rgba(0, 0, 0, 0.12)" : "0px 10px 30px rgba(0, 0, 0, 0.08)",
                    },
                  }}
                  onClick={() => {
                    if (isBooked) {
                      setSelectedEvent(event);
                      setSelectedEventInstructors(instructorNames);
                      setDrawerOpen(true);
                    } else if (!isPast) {
                      handleBook(event, instructorNames);
                    }
                  }}
                >
                  {isBooked && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        zIndex: 10,
                      }}
                    >
                      <Chip
                        icon={<CheckCircleOutlineIcon sx={{ fontSize: "14px !important", color: "#2E7D32 !important" }} />}
                        label="Joined"
                        size="small"
                        sx={{
                          backgroundColor: "#E8F5E9",
                          color: "#2E7D32",
                          fontWeight: 700,
                          fontSize: "11px",
                          height: "24px",
                        }}
                      />
                    </Box>
                  )}

                  <Box sx={{ position: "relative", height: "240px", overflow: "hidden" }}>
                    {event.thumbnail ? (
                      <Box
                        component="img"
                        src={
                          event.thumbnail.startsWith("data:")
                            ? event.thumbnail
                            : event.thumbnail.startsWith("http")
                              ? event.thumbnail
                              : `data:image/*;base64,${event.thumbnail}`
                        }
                        alt={event.eventName}
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          backgroundColor: "#f5f5f5",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      />
                    )}

                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "60%",
                        background:
                          "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)",
                      }}
                    />

                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 16,
                        left: 16,
                        right: 16,
                        color: "#fff",
                      }}
                    >
                      <TruncatedTitle
                        text={event.eventName}
                        variant="h5"
                        sx={{
                          fontSize: "1.8rem",
                          mb: 0.5,
                          lineHeight: 1.1,
                          fontWeight: 800,
                        }}
                      />

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-end",
                          gap: 1.5,
                          width: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.25,
                            mb: 0.25,
                            minWidth: 0,
                            flex: 1,
                          }}
                        >
                          <LocationOnOutlinedIcon
                            sx={{ color: "#fff", fontSize: "1rem", fontWeight: 700 }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              opacity: 0.9,
                              fontWeight: 700,
                              fontSize: "0.9rem",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {event.location}
                          </Typography>
                        </Box>

                        <Tooltip
                          title={`Instructor: ${instructorNames}`}
                          arrow
                          placement="top-end"
                          disableHoverListener={!shouldShowInstructorTooltip}
                          disableFocusListener={!shouldShowInstructorTooltip}
                          disableTouchListener={!shouldShowInstructorTooltip}
                          slotProps={{ popper: { sx: instructorTooltipSx } }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              opacity: 0.9,
                              fontWeight: 500,
                              fontSize: "0.95rem",
                              flex: 1,
                              minWidth: 0,
                              textAlign: "right",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              cursor: shouldShowInstructorTooltip ? "default" : "inherit",
                            }}
                          >
                            Instructor : {instructorNames}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>

                  <CardContent
                    sx={{
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      flexGrow: 1,
                    }}
                  >
                    {/* Date row */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                      <Box
                        sx={{
                          color: themestyle.colors.activeGreen,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CalendarTodayOutlinedIcon sx={{ fontSize: 22 }} />
                      </Box>
                      <Typography
                        sx={{
                          color: "#1A202C",
                          fontWeight: 700,
                          fontSize: "0.95rem",
                          lineHeight: 1.3,
                        }}
                      >
                        {formattedDateString} {formatEventTime(event.time)}
                      </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    {/* Price + offer block */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "space-between",
                        mb: 2,
                        minHeight: "72px",
                      }}
                    >
                      {/* Left: price */}
                      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", height: "100%" }}>
                        {isFreeEvent ? (
                          <>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#6B7280",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                fontWeight: 800,
                                lineHeight: 1,
                                mb: 0.2,
                              }}
                            >
                              Free event
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "26px",
                                fontWeight: 800,
                                color: "#6B7280",
                                lineHeight: 1,
                              }}
                            >
                              Free
                            </Typography>
                          </>
                        ) : hasActiveOffer ? (
                          <>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#9CA3AF",
                                textDecoration: "line-through",
                                lineHeight: 1,
                                mb: 0.2,
                              }}
                            >
                              {fmtPrice(event.standardPrice)}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "26px",
                                fontWeight: 800,
                                color: "#5AA839",
                                lineHeight: 1,
                              }}
                            >
                              {fmtPrice(event.offerPrice)}
                            </Typography>
                          </>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: "26px",
                              fontWeight: 800,
                              color: event.standardPrice === 0 ? "#6B7280" : "#111827",
                              lineHeight: 1,
                            }}
                          >
                            {event.standardPrice === 0 ? "Free" : fmtPrice(event.standardPrice)}
                          </Typography>
                        )}
                      </Box>

                      {/* Right: offer badge + promo code */}
                      {hasActiveOffer && (
                        <Box
                          sx={{
                            textAlign: "right",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            justifyContent: "flex-end",
                            minHeight: 54,
                          }}
                        >
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.4,
                              backgroundColor: "#F0FBE8",
                              border: "1px solid #C6ECA8",
                              borderRadius: "8px",
                              px: 1,
                              py: 0.4,
                              mb: 0.5,
                            }}
                          >
                            <LocalOfferOutlinedIcon sx={{ fontSize: 12, color: "#5AA839" }} />
                            <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#5AA839" }}>
                              {eventDiscountPct}% OFF
                            </Typography>
                          </Box>
                          {event.promoCode && (
                            <Typography
                              sx={{
                                fontSize: "11px",
                                color: "#6B7280",
                                display: "block",
                                lineHeight: 1.15,
                              }}
                            >
                              Code:{" "}
                              <Box
                                component="span"
                                sx={{
                                  fontWeight: 700,
                                  color: "#374151",
                                  backgroundColor: "#F3F4F6",
                                  px: 0.6,
                                  py: 0.1,
                                  borderRadius: "4px",
                                  fontFamily: "monospace",
                                  fontSize: "11px",
                                }}
                              >
                                {event.promoCode}
                              </Box>
                            </Typography>
                          )}
                          {event.offerEndDate && (
                            <Typography sx={{ fontSize: "10px", color: "#9CA3AF", mt: 0.15, lineHeight: 1.1 }}>
                              Till{" "}
                              {new Date(event.offerEndDate).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ display: "flex", gap: 1.5, mt: "auto" }}>
                      {/* Add to Cart icon button — only for open, unbooked events */}
                      {!isBooked && !isPast && (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isInCart(event.id)) {
                              addItem({
                                id: event.id,
                                type: "event",
                                name: event.eventName,
                                originalPrice: event.standardPrice || 0,
                                finalPrice: finalEventPrice || 0,
                                discount: hasActiveOffer ? eventDiscountPct : undefined,
                                discountLabel: hasActiveOffer && event.promoCode ? event.promoCode : undefined,
                                discountCode: hasActiveOffer ? event.promoCode : undefined,
                                instructorName: instructorNames !== "TBA" ? instructorNames : undefined,
                                image: event.thumbnail || undefined,
                                date: formatDate(event.startDate),
                                time: formatEventTime(event.time),
                              });
                              setSnackbarMessage(`${event.eventName} added to cart!`);
                              setSnackbarType("success");
                              setSnackbarOpen(true);
                            }
                          }}
                          sx={{
                            backgroundColor: isInCart(event.id) ? "#F0FBE8" : "#5AA839",
                            color: isInCart(event.id) ? "#6CBC44" : "#fff",
                            border: isInCart(event.id) ? "1.5px solid #6CBC44" : "none",
                            borderRadius: "10px",
                            width: "40px",
                            height: "40px",
                            flexShrink: 0,
                            "&:hover": {
                              backgroundColor: isInCart(event.id) ? "#F0FBE8" : "#4A8F2A",
                            },
                            "&:disabled": {
                              backgroundColor: "#F0FBE8",
                              color: "#6CBC44",
                            },
                          }}
                        >
                          {isInCart(event.id) ? (
                            <CheckCircleIcon sx={{ fontSize: 18 }} />
                          ) : (
                            <AddShoppingCartIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      )}

                      {/* Buy Now / status button */}
                      <Button
                        fullWidth
                        variant="contained"
                        disabled={isPast && !isBooked}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isBooked) {
                            setSelectedEvent(event);
                            setSelectedEventInstructors(instructorNames);
                            setDrawerOpen(true);
                          } else if (!isPast) {
                            handleBook(event, instructorNames);
                          }
                        }}
                        sx={{
                          backgroundColor: isBooked ? "#E8F5E9" : (isPast && !isBooked) ? "#FFEBEE" : "#5AA839",
                          color: isBooked ? "#2E7D32" : (isPast && !isBooked) ? "#D32F2F" : "#fff",
                          borderRadius: "10px",
                          textTransform: "none",
                          fontSize: "14px",
                          py: 1,
                          fontWeight: 600,
                          boxShadow: "none",
                          "&:hover": {
                            backgroundColor: isBooked ? "#E8F5E9" : (isPast && !isBooked) ? "#FFEBEE" : "#4A8F2A",
                            boxShadow: isBooked || (isPast && !isBooked)
                              ? "none"
                              : "0px 4px 12px rgba(90, 168, 57, 0.2)",
                          },
                          "&:disabled": {
                            backgroundColor: (isPast && !isBooked) ? "#FFEBEE" : "#E8F5E9",
                            color: (isPast && !isBooked) ? "#D32F2F" : "#2E7D32",
                          },
                        }}
                      >
                        {isBooked ? (
                          "Joined"
                        ) : isPast ? (
                          "Closed"
                        ) : isFreeEvent ? (
                          "Book Free"
                        ) : (
                          "Buy Now"
                        )}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Server-side pagination controls */}
      {!loading && totalCount > rowsPerPage && (
        <Box sx={{ mt: 4, mb: 2 }}>
          {isMobile ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                py: 2,
                px: 2,
                backgroundColor: "#ffffff",
                borderTop: "1px solid #F3F4F6",
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontSize: "14px", color: "#1F2937", fontWeight: 500, mr: 2 }}
              >
                {startItem} – {endItem}
                <span style={{ marginLeft: "8px", marginRight: "8px", color: "#9CA3AF" }}>
                  of
                </span>
                {totalCount}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  sx={{
                    width: "32px",
                    height: "32px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    color: "#1F2937",
                    "&:disabled": { color: "#D1D5DB", borderColor: "#F3F4F6" },
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M10 12L6 8L10 4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  sx={{
                    width: "32px",
                    height: "32px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    color: "#1F2937",
                    "&:disabled": { color: "#D1D5DB", borderColor: "#F3F4F6" },
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M6 4L10 8L6 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </IconButton>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 2,
                borderTop: "1px solid #E5E7EB",
                pt: 2,
              }}
            >
              <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                {startItem}–{endItem} of {totalCount}
              </Typography>
              <CustomPaginationActions
                count={totalCount}
                page={page - 1}
                rowsPerPage={rowsPerPage}
                onPageChange={(_: any, newPage: number) => setPage(newPage + 1)}
              />
            </Box>
          )}
        </Box>
      )}

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
      />

      <EventPurchaseDrawer
        open={drawerOpen}
        event={selectedEvent}
        instructorNames={selectedEventInstructors}
        onClose={() => setDrawerOpen(false)}
        returnPath="/events"
        onPurchaseComplete={(message, type) => {
          setSnackbarMessage(message);
          setSnackbarType(type);
          setSnackbarOpen(true);
          if (type === "success") {
            setDrawerOpen(false);
            refetchMyBookings();
          }
        }}
        isBooked={selectedEvent ? bookedEventIds.has(selectedEvent.id) : false}
      />
    </Box>
  );
};

export default UserEventCardView;
